import type { FieldDefinition } from '../types';

/** Maps a FieldDefinition to a single form control. Deliberately plain HTML inputs, styled by
 * the host page's cascade plus the small stylesheet lightbox.ts injects into its shadow root —
 * no framework, this runs inside an arbitrary third-party page. */
export function renderField(field: FieldDefinition): HTMLElement {
  const wrapper = document.createElement('label');
  wrapper.className = 'cms-lf-field';
  wrapper.textContent = field.name + (field.required ? ' *' : '');

  const control = buildControl(field);
  control.setAttribute('name', field.slug);
  if (field.required) control.setAttribute('required', 'true');
  wrapper.appendChild(control);
  return wrapper;
}

function buildControl(field: FieldDefinition): HTMLElement {
  if (field.type === 'SELECT' && field.options) {
    const select = document.createElement('select');
    for (const option of field.options) {
      const opt = document.createElement('option');
      opt.value = option;
      opt.textContent = option;
      select.appendChild(opt);
    }
    return select;
  }

  if (field.type === 'LONG_TEXT' || field.type === 'RICH_TEXT') {
    return document.createElement('textarea');
  }

  const input = document.createElement('input');
  input.type = inputType(field);
  return input;
}

function inputType(field: FieldDefinition): string {
  switch (field.type) {
    case 'EMAIL':
      return 'email';
    case 'URL':
      return 'url';
    case 'NUMBER':
      return 'number';
    case 'DATE':
      return 'date';
    case 'DATETIME':
      return 'datetime-local';
    case 'BOOLEAN':
      return 'checkbox';
    default:
      return 'text';
  }
}

export function readFieldValues(form: HTMLFormElement, fields: FieldDefinition[]): Record<string, unknown> {
  const data = new FormData(form);
  const result: Record<string, unknown> = {};
  for (const field of fields) {
    if (field.type === 'BOOLEAN') {
      result[field.slug] = form.elements.namedItem(field.slug) instanceof HTMLInputElement
        && (form.elements.namedItem(field.slug) as HTMLInputElement).checked;
    } else {
      result[field.slug] = data.get(field.slug) ?? '';
    }
  }
  return result;
}
